
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import ElementProgress from '@/components/ElementProgress.vue';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import PhotosModule from '@/store/modules/Photos';
import SessionSettings from './session/SessionSettings.vue';
import SessionInfoDialog from './session/SessionInfoDialog.vue';
import Dropzone, { FileStructure } from '../decorators/Dropzone';
import SessionsModule from '../store/modules/Sessions';
import SessionSummaryWindow from './command-center/SessionSummaryWindow.vue';
import ElementModel from '@/model/SessionListElement';
import SessionOrdersWindow from './command-center/SessionOrdersWindow.vue';
import UploadModule from '@/store/modules/Upload';
import Watermark from '@/model/Watermark';
import WatermarkDialog from './WatermarkDialog.vue';
import api from '../service/api';
import Session from '@/model/Session';
import ClientLink from '@/model/ClientLinkListElement';
import { createInterfaceSound, playInterfaceSound } from '@/service/interfaceSound';
import {
    confirmRawConversion,
    waitForRawConversionDialogClose,
} from '@/service/rawConversionConfirmation';

const photosModule = getModule(PhotosModule, store);
const uploadModule = getModule(UploadModule, store);
const sessionsModule = getModule(SessionsModule, store);

@Component({
    components: {
        ElementProgress,
        SessionSettings,
        SessionInfoDialog,
        SessionSummaryWindow,
        SessionOrdersWindow,
        WatermarkDialog,
    },
})
@Dropzone
export default class SessionListElement extends Vue {
    @Prop({ required: true })
    private session!: ElementModel;
    watermarkDialog: boolean = false;
    imageLoading: boolean = false;
    links: ClientLink[] = [];
    linksLoaded: boolean = false;
    shareToggleLoading: boolean = false;
    cardPressed: boolean = false;
    editingName: boolean = false;
    editedName: string = '';
    renaming: boolean = false;
    nameOverflow: number = 0;
    nameCycle: number = 10;
    private menuSound: HTMLAudioElement;
    private pendingOpen = false;
    private pressStartX = 0;
    private pressStartY = 0;
    private readonly pressMoveThreshold = 10;
    private boundWindowMouseUp?: (event: MouseEvent) => void;
    private boundWindowMouseMove?: (event: MouseEvent) => void;

    constructor() {
        super();
        this.menuSound = createInterfaceSound('/sound/slide.wav');
    }

    mounted() {
        this.$on('globalDragstart', () => this.$el.classList.add('tileDrop'));
        this.$on('globalDragend', () => this.$el.classList.remove('tileDrop'));
        this.$on('dragenter', () => this.$el.classList.add('dropActive'));
        this.$on('dragleave', () => this.$el.classList.remove('dropActive'));
        this.$on('drop', (files: FileStructure) => {
            this.handleDrop(files);
        });
        // Removed automatic loadLinksIfNeeded() - only load when user actually needs it

        this.boundWindowMouseUp = (event: MouseEvent) => this.handleWindowMouseUp(event);
        this.boundWindowMouseMove = (event: MouseEvent) => this.handleWindowMouseMove(event);
        window.addEventListener('mouseup', this.boundWindowMouseUp);
        window.addEventListener('mousemove', this.boundWindowMouseMove);
        this.updateNamePan();
    }

    beforeDestroy() {
        if (this.boundWindowMouseUp) {
            window.removeEventListener('mouseup', this.boundWindowMouseUp);
        }
        if (this.boundWindowMouseMove) {
            window.removeEventListener('mousemove', this.boundWindowMouseMove);
        }
    }

    get loadedSession() {
        return this.loadedSessions[this.session.id];
    }

    public async handleDrop({ allFiles: files }: FileStructure) {
        const rawConversionDecision = await confirmRawConversion(files);
        if (!rawConversionDecision.confirmed) {
            return;
        }

        if (!this.loadedSessions[this.session.id]) {
            await sessionsModule.loadSession(this.session.id);
        }

        if (!this.loadedSession.watermark) {
            await waitForRawConversionDialogClose(files);
            this.watermarkDialog = true;
            await sessionsModule.chooseWatermark({
                sessionId: this.loadedSession.id,
                watermark: await new Promise<Watermark>((resolve) =>
                    (this.$refs.watermarkDialog as Vue).$once('watermarkResolved', resolve),
                ),
            });
        }

        uploadModule.uploadFiles({
            session: this.loadedSessions[this.session.id],
            files,
            rawConversionDecision,
        });
    }

    get thumbnail() {
        const sessionPhotosList = photosModule.sessionPhotos[this.session.id];
        const sessionThumbnail = this.session.thumbnail;
        if (
            sessionPhotosList &&
            sessionPhotosList.length > 0 &&
            sessionThumbnail &&
            sessionThumbnail.photoId &&
            sessionThumbnail.photoId.trim() !== '' &&
            sessionPhotosList.some(({ id }) => id === sessionThumbnail.photoId)
        ) {
            return sessionPhotosList
                .find(({ id }) => id === sessionThumbnail.photoId)!
                .smallThumbUrl;
        } else if (sessionThumbnail && sessionThumbnail.url && sessionThumbnail.url.trim() !== '') {
            return sessionThumbnail.url;
        } else if (sessionPhotosList && sessionPhotosList.length > 0) {
            return sessionPhotosList[0].smallThumbUrl;
        }
    }

    get loadedSessions() {
        return sessionsModule.sessions;
    }

    get progress() {
        return uploadModule.sessionProgress[this.session.id];
    }

    get progressStatus() {
        return uploadModule.sessionInProgressIds.includes(
            this.session.id,
        ) ? 'pending' : 'finished';
    }

    async handleImageClick(event?: MouseEvent) {
        if (this.imageLoading) return;
        if (event) {
            window.dispatchEvent(new CustomEvent('raws-ui-ripple', {
                detail: {
                    x: event.clientX,
                    y: event.clientY,
                },
            }));
        }
        this.imageLoading = true;

        try {
            await this.$router.push({ name: 'editSession', params: { id: this.session.id } });
        } finally {
            this.imageLoading = false;
        }
    }

    startNameEdit() {
        this.editedName = this.session.name;
        this.editingName = true;
        this.$nextTick(() => (this.$refs.nameInput as HTMLInputElement).focus());
    }

    @Watch('session.name')
    @Watch('editingName')
    updateNamePan() {
        this.nameOverflow = 0;
        this.$nextTick(() => {
            const text = this.$refs.sessionNameText as HTMLElement | undefined;
            const content = this.$refs.sessionNameContent as HTMLElement | undefined;
            this.nameOverflow = text && content ? Math.max(0, content.getBoundingClientRect().width - text.clientWidth) : 0;
            this.nameCycle = Math.max(10, this.nameOverflow / 18 + 8);
        });
    }

    cancelNameEdit() {
        this.editingName = false;
    }

    async saveName() {
        if (!this.editingName || this.renaming) return;

        const name = this.editedName.trim();
        if (!name || name === this.session.name) {
            this.cancelNameEdit();
            return;
        }

        this.renaming = true;
        try {
            await sessionsModule.loadSessionIfNotExists(this.session.id);
            const current = sessionsModule.sessions[this.session.id];
            const updated = new Session(
                current.id, name, current.watermark, current.pricelistId,
                current.finalizationEnabled, current.downloadEnabled, current.finalized,
                current.finalizationMode, current.orderCount, current.chatEnabled,
                current.welcomeMessageSetId,
            );
            await api.updateSession(updated);
            sessionsModule.patchSession(updated);
            (this as any).$vToastify.success({ title: 'Sukces!', body: 'Zmieniono nazwę' });
            this.editingName = false;
        } catch (error) {
            console.error('Failed to rename session:', error);
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się zmienić nazwy' });
            this.$nextTick(() => (this.$refs.nameInput as HTMLInputElement).focus());
        } finally {
            this.renaming = false;
        }
    }

    get sessionHasLinks() {
        return this.links.length > 0;
    }

    get sessionShared() {
        // If links haven't been loaded yet, fallback to session.shared
        if (!this.linksLoaded) {
            return this.session.shared;
        }
        return this.links.some(link => !link.disabled);
    }

    get canToggleShare() {
        // Only allow toggling if links are loaded, there are links, and not currently loading
        return this.linksLoaded && this.sessionHasLinks && !this.shareToggleLoading;
    }

    get shouldShowTooltip() {
        // Show tooltip when links are loaded but there are no links (and not loading)
        return this.linksLoaded && !this.sessionHasLinks && !this.shareToggleLoading;
    }



    async loadLinksIfNeeded() {
        if (!this.linksLoaded) {
            try {
                this.links = await api.getClientLinks(this.session.id);
                this.linksLoaded = true;
            } catch (error) {
                console.error('Failed to load session links:', error);
            }
        }
    }

    handleMenuToggle(isOpen: boolean) {
        if (isOpen) {
            playInterfaceSound(this.menuSound).catch(error => {
                // Silent error handling for audio playback
                console.warn('Failed to play menu sound:', error);
            });
        }
    }

    async toggleSessionShare() {
        await this.loadLinksIfNeeded();
        
        if (!this.canToggleShare || this.shareToggleLoading) {
            return; // Button should be disabled, but just in case
        }

        const newShareStatus = !this.sessionShared;
        this.shareToggleLoading = true;
        
        try {
            if (newShareStatus) {
                await api.enableSessionLinks(this.session.id);
                sessionsModule.markSessionShared(this.session.id);
                (this as any).$vToastify.success({ 
                    title: "Sukces", 
                    body: "Sesja została udostępniona" 
                });
            } else {
                await api.disableSessionLinks(this.session.id);
                sessionsModule.markSessionNotShared(this.session.id);
                (this as any).$vToastify.success({ 
                    title: "Sukces", 
                    body: "Sesja została wyłączona" 
                });
            }
            
            // Update local links state
            this.links = this.links.map(l => ({ ...l, disabled: !newShareStatus }));
        } catch (error) {
            console.error('Failed to toggle session share status:', error);
            (this as any).$vToastify.error({ 
                title: "Błąd", 
                body: "Nie udało się zmienić statusu sesji" 
            });
        } finally {
            this.shareToggleLoading = false;
        }
    }

    handleCardMouseDown(event: MouseEvent) {
        this.cardPressed = true;
        this.pendingOpen = true;
        this.pressStartX = event.clientX;
        this.pressStartY = event.clientY;
    }

    handleCardMouseUp() {
        if (this.pendingOpen) {
            return;
        }
        this.cardPressed = false;
    }

    handleWindowMouseMove(event: MouseEvent) {
        if (!this.pendingOpen) {
            return;
        }

        const deltaX = event.clientX - this.pressStartX;
        const deltaY = event.clientY - this.pressStartY;
        if (Math.hypot(deltaX, deltaY) > this.pressMoveThreshold) {
            this.pendingOpen = false;
            this.cardPressed = false;
        }
    }

    async handleWindowMouseUp(event: MouseEvent) {
        if (!this.pendingOpen) {
            this.cardPressed = false;
            return;
        }

        this.pendingOpen = false;
        this.cardPressed = false;
        await this.handleImageClick(event);
    }

    public playSpinSound() {
        const audio = createInterfaceSound('/sound/spin.wav');
        playInterfaceSound(audio).catch(error => {
            console.log('Audio playback failed:', error);
        });
    }
}
